import { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { getAdminAuditLog } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Clock } from 'lucide-react';

export default function AdminAuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async (cursor = null) => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (cursor) params.cursor = cursor;
      
      const res = await getAdminAuditLog(params);
      if (cursor) {
        setLogs(prev => [...prev, ...res.data.items]);
      } else {
        setLogs(res.data.items || []);
      }
      setNextCursor(res.data.next_cursor);
      setHasMore(res.data.has_more);
    } catch (err) {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action) => {
    const styles = {
      approve_host: 'bg-green-100 text-green-700',
      decline_host: 'bg-red-100 text-red-700',
      suspend_user: 'bg-orange-100 text-orange-700',
      suspend_property: 'bg-orange-100 text-orange-700',
      delete_property: 'bg-red-100 text-red-700',
      refund_booking: 'bg-blue-100 text-blue-700',
      hide_review: 'bg-slate-100 text-slate-700',
      unhide_review: 'bg-slate-100 text-slate-700'
    };
    return (
      <Badge className={styles[action] || 'bg-slate-100 text-slate-700'}>
        {action.replace(/_/g, ' ')}
      </Badge>
    );
  };

  const formatMeta = (meta) => {
    if (!meta || Object.keys(meta).length === 0) return null;
    return Object.entries(meta)
      .filter(([k, v]) => v)
      .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`)
      .join(' | ');
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-audit-log-page">
      <div>
        <h1 className="text-2xl font-bold">Admin Audit Log</h1>
        <p className="text-slate-500">Track all administrative actions</p>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-slate-600">Time</th>
                  <th className="text-left p-4 font-medium text-slate-600">Admin</th>
                  <th className="text-left p-4 font-medium text-slate-600">Action</th>
                  <th className="text-left p-4 font-medium text-slate-600">Entity</th>
                  <th className="text-left p-4 font-medium text-slate-600">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {logs.map(log => (
                  <tr key={log.id} className="hover:bg-slate-50">
                    <td className="p-4 text-sm">
                      <div className="flex items-center gap-1 text-slate-500">
                        <Clock className="w-3 h-3" />
                        {new Date(log.created_at).toLocaleString()}
                      </div>
                    </td>
                    <td className="p-4">
                      <div>
                        <p className="font-medium text-sm">{log.admin_name}</p>
                        <p className="text-xs text-slate-500">{log.admin_email}</p>
                      </div>
                    </td>
                    <td className="p-4">{getActionBadge(log.action)}</td>
                    <td className="p-4 text-sm">
                      <span className="text-slate-500">{log.entity_type}:</span>
                      <span className="ml-1 font-mono text-xs">{log.entity_id.slice(0, 8)}...</span>
                    </td>
                    <td className="p-4 text-sm text-slate-600">
                      {formatMeta(log.meta) || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {logs.length === 0 && !loading && (
            <div className="text-center py-12 text-slate-500">
              No audit log entries yet
            </div>
          )}
        </CardContent>
      </Card>

      {hasMore && (
        <div className="text-center">
          <Button variant="outline" onClick={() => loadLogs(nextCursor)} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Load More
          </Button>
        </div>
      )}
    </div>
  );
}
